import { useTranslation } from 'react-i18next';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List from 'react-virtualized/dist/commonjs/List';
import { ReactComponent as ALertIcon } from 'src/assets/tailwind/alert-circle.svg';
import { ReactComponent as CheckIcon } from 'src/assets/tailwind/check-circle-filled.svg';
import { ReactComponent as FolderIcon } from 'src/assets/tailwind/document/folder.svg';
import { ReactComponent as XCircleIcon } from 'src/assets/tailwind/x-circle.svg';
import Avatar from 'src/components/ui/avatar';
import { Button } from 'src/components/ui/button';
import { Icon } from 'src/components/ui/icon';
import { Spinner } from 'src/components/ui/spinner';
import { Typography } from 'src/components/ui/typography';
import { FILE_UPLOADING_STATUS, RESOURCE_TYPE } from 'src/types/enum';
import type { UploadFile } from 'src/utils/constants';
import { FileExtensionIconMapper } from 'src/utils/files/file';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { formatBytes, getFileExtension } from 'src/utils/utils';

export function FileItem({
  file,
  icon,
  onRemove
}: {
  file: UploadFile;
  icon?: React.ElementType;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  const extension = getFileExtension(file);
  return (
    <div className="tw:w-full tw:flex tw:items-center tw:justify-between tw:rounded-2xl tw:border tw:border-border-secondary tw:px-l tw:py-m">
      <div className="tw:flex tw:items-center tw:gap-s tw:min-w-0">
        <Avatar
          type="file"
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          src={icon ?? FileExtensionIconMapper[RESOURCE_TYPE.FILE][extension]}
          variant="square"
          fallbackImage={FolderIcon}
          colorScheme="none"
        />
        <div className="tw:min-w-0">
          <Typography
            variant="h6"
            colorClass="tw:text-text-primary"
            className="tw:text-sm tw:font-medium tw:w-full tw:truncate tw:line-clamp-1"
          >
            {file.name}
          </Typography>
          <Typography
            variant="b3"
            colorClass="tw:text-text-secondary tw:flex tw:items-center tw:gap-s"
            className="tw:text-xs tw:text-gray-500"
          >
            {formatBytes(file.size)}
            {file.status === FILE_UPLOADING_STATUS.UPLOADED && (
              <div className="tw:text-text-success tw:flex tw:items-center tw:gap-2xs">
                <Icon icon={CheckIcon} size={12} />
                <span>{t(T.uploaded)}</span>
              </div>
            )}
            {file.status === FILE_UPLOADING_STATUS.PREPARING && (
              <Spinner
                size="xs"
                text={t(T.preparing)}
                className="tw:flex-row tw:items-center tw:text-icon-brand"
              />
            )}
            {file.status === FILE_UPLOADING_STATUS.UPLOADING && (
              <Spinner
                size="xs"
                text={t(T.uploading)}
                className="tw:flex-row tw:items-center tw:text-icon-brand"
              />
            )}
            {(file.status === FILE_UPLOADING_STATUS.FAILED ||
              file.status === FILE_UPLOADING_STATUS.DROPPED) && (
              <div className="tw:text-text-error tw:flex tw:items-center tw:gap-2xs">
                <Icon icon={ALertIcon} size={12} />
                <span>{t(T.failed)}</span>
                <span className="tw:text-text-secondary">({file.error})</span>
              </div>
            )}
          </Typography>
        </div>
      </div>

      {!(
        file.status === FILE_UPLOADING_STATUS.UPLOADED ||
        file.status === FILE_UPLOADING_STATUS.PREPARING ||
        file.status === FILE_UPLOADING_STATUS.UPLOADING
      ) && (
        <Button
          onClick={() => onRemove(file.id)}
          leftIcon={XCircleIcon}
          iconOnly
          size="small"
          hierarchy="ghost"
        />
      )}
    </div>
  );
}

const FILE_ITEM_HEIGHT = 72;
const FILE_ITEM_GAP = 12;

export function FileList({
  files,
  onRemove
}: {
  files: UploadFile[];
  onRemove: (id: string) => void;
}) {
  return (
    <div className="tw:w-full tw:h-full tw:min-h-0">
      <AutoSizer>
        {({ width, height }) => (
          <List
            width={width}
            height={height}
            rowCount={files.length}
            rowHeight={FILE_ITEM_HEIGHT + FILE_ITEM_GAP}
            overscanRowCount={6}
            rowRenderer={({ index, key, style }) => (
              <div key={key} style={style} className="tw:pb-m">
                <FileItem file={files[index]} onRemove={onRemove} />
              </div>
            )}
          />
        )}
      </AutoSizer>
    </div>
  );
}
